import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { Loader2 } from "lucide-react";

interface PermissionGuardProps {
  children: ReactNode;
  permission: "conversations" | "crm" | "agents" | "products" | "integrations" | "settings" | "appointments" | "dashboard" | "billing" | "automations" | "tasks" | "deals";
  fallbackPath?: string;
}

export function PermissionGuard({ 
  children, 
  permission, 
  fallbackPath = "/tasks" 
}: PermissionGuardProps) {
  const location = useLocation();
  const {
    loading,
    permissions,
    canAccessConversations,
    canAccessCRM,
    canAccessAgents,
    canAccessProducts,
    canAccessIntegrations,
    canAccessSettings,
    canAccessAppointments,
    canAccessDashboard,
    canAccessBilling,
    canAccessAutomations,
    canAccessTasks,
    canAccessDeals,
  } = useMemberPermissions();

  if (loading || permissions === null) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const permissionMap: Record<string, boolean> = {
    conversations: canAccessConversations,
    crm: canAccessCRM,
    agents: canAccessAgents,
    products: canAccessProducts,
    integrations: canAccessIntegrations,
    settings: canAccessSettings,
    appointments: canAccessAppointments,
    dashboard: canAccessDashboard,
    billing: canAccessBilling,
    automations: canAccessAutomations,
    tasks: canAccessTasks,
    deals: canAccessDeals,
  };

  const hasPermission = permissionMap[permission];

  if (!hasPermission) {
    if (fallbackPath === location.pathname) {
      return <Navigate to="/settings" replace />;
    }
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
